/**
 * Client-code canonicalization at the session boundary (FR-003, contract
 * N1 of the AITH-30 realtime-negotiate contract): identity claims may carry
 * any casing, but the realtime backend derives its publication group with
 * ClientCodeCanonicalizer and SignalR group names are case-sensitive, so the
 * session exposes only the canonical form and every downstream comparison
 * stays exact.
 *
 * Mirrors aith-api AITH.Domain/Tenancy/ClientCodeCanonicalizer exactly: trim,
 * validate the RAW value against the ASCII contract, then uppercase. Keep the
 * two in lockstep — the validator tests assert the same boundaries.
 *
 * The ORDER is load-bearing, and the server-side implementation documents why:
 * `toUpperCase()` maps some non-ASCII characters onto ASCII letters (U+017F
 * 'ſ' uppercases to 'S'), so folding case *before* validating would let a code
 * the authorization layer rejects — `ſ001` — masquerade as another tenant's
 * canonical code, `S001`. Corrected under AITH-44 (2026-08-26), which is the
 * first feature to feed an untrusted, URL-supplied client code through here:
 * before it, only identity-claim values reached this function.
 */

/** Case-insensitive, so the RAW (unfolded) value is what must be ASCII-legal. */
const RAW_CLIENT_CODE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/;

export function canonicalizeClientCode(clientCode: string | null): string | null {
  if (clientCode === null) return null;
  const raw = clientCode.trim();
  return RAW_CLIENT_CODE_PATTERN.test(raw) ? raw.toUpperCase() : null;
}
